import React, { useState, useEffect, FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  User as FirebaseUser,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/config";
import Config from "../../Config";
import { useAuth } from "../../utils/AuthContext";
import { useSendEmail } from "../../hooks/useSendEmail";
import { verificationCodeEmail } from "../../emails";

import PublicNavbar from "../../components/PublicNavbar";
import Footer from "../../components/Footer";
import LoadingPage from "../../components/LoadingPage";

import { Eye, EyeOff, TriangleAlert, CircleX } from "lucide-react";

import PrimaryButton from "../../components/Buttons/PrimaryButton";
import SecondaryButton from "../../components/Buttons/SecondaryButton";
import PasswordStrengthMeter, {
  getPasswordScore,
} from "../../components/PasswordStrengthMeter";

interface InvitationData {
  email: string;
  organization_name: string;
  role: string;
  expires_at: string;
  user_exists: boolean;
  is_keycloak_user: boolean;
}

const OrganizationAccept: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    backendToken,
    user: authUser,
    loginWithKeycloak,
    refreshSubscriptionStatus,
  } = useAuth();

  const [invitationData, setInvitationData] = useState<InvitationData | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [apiError, setApiError] = useState<string>("");
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);

  const [name, setName] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [existingUserPassword, setExistingUserPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showExistingUserPassword, setShowExistingUserPassword] =
    useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { sendEmail } = useSendEmail();

  const token = searchParams.get("token");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchInvitationData = async () => {
      if (!token) {
        setError("Invalid invitation link. No token provided.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${Config.API_URL}/api/organization/invitation/${token}`,
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || "Failed to validate invitation");
        }

        const data = await response.json();
        if (!cancelled) {
          setInvitationData({
            email: data.email,
            organization_name: data.organization_name,
            role: data.role,
            expires_at: data.expires_at,
            user_exists: data.user_exists,
            is_keycloak_user: data.is_keycloak_user ?? false,
          });
          setLoading(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(
            err.message ||
              "Failed to load invitation data. The link may be invalid or expired.",
          );
          setLoading(false);
        }
      }
    };

    fetchInvitationData();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleKeycloakLogin = () => {
    if (token) {
      sessionStorage.setItem("pendingOrgAcceptToken", token);
    }
    loginWithKeycloak();
  };

  const handleExistingUserAccept = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError("");

    if (!invitationData) {
      setApiError("Invalid invitation data");
      return;
    }

    setSubmitting(true);

    try {
      // Keycloak path — no Firebase token needed
      if (invitationData.is_keycloak_user) {
        if (!backendToken) {
          setApiError("Please log in with PSDI first.");
          setSubmitting(false);
          return;
        }

        const acceptResponse = await fetch(
          `${Config.API_URL}/api/organization/accept-invitation`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${backendToken}`,
            },
            body: JSON.stringify({ token }),
          },
        );

        if (!acceptResponse.ok) {
          const errorData = await acceptResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || "Failed to accept invitation");
        }

        await refreshSubscriptionStatus();
        navigate("/home/dashboard", { replace: true });
        return;
      }

      // Firebase path

      // If already logged in with a valid backend token, use it directly
      if (currentUser && backendToken) {
        const firebaseToken = await currentUser.getIdToken();
        const acceptResponse = await fetch(
          `${Config.API_URL}/api/organization/accept-invitation`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${backendToken}`,
              "X-Firebase-Authorization": `Bearer ${firebaseToken}`,
            },
            body: JSON.stringify({ token }),
          },
        );

        if (!acceptResponse.ok) {
          const errorData = await acceptResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || "Failed to accept invitation");
        }

        await refreshSubscriptionStatus();
        navigate("/home/dashboard", { replace: true });
        return;
      }

      let firebaseToken: string;

      if (!currentUser) {
        if (!existingUserPassword.trim()) {
          setApiError("Please enter your password");
          setSubmitting(false);
          return;
        }

        try {
          const userCredential = await signInWithEmailAndPassword(
            auth,
            invitationData.email,
            existingUserPassword,
          );
          firebaseToken = await userCredential.user.getIdToken();
        } catch (loginError: any) {
          console.error("Login error:", loginError);
          if (
            loginError.code === "auth/wrong-password" ||
            loginError.code === "auth/invalid-credential"
          ) {
            setApiError("Incorrect password. Please try again.");
          } else if (loginError.code === "auth/too-many-requests") {
            setApiError("Too many attempts. Please try again later.");
          } else {
            setApiError(
              loginError.message || "Failed to log in. Please try again.",
            );
          }
          setSubmitting(false);
          return;
        }
      } else {
        firebaseToken = await currentUser.getIdToken();
      }

      const loginResponse = await fetch(
        `${Config.API_URL}/api/auth/firebase/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${firebaseToken}`,
          },
        },
      );

      if (!loginResponse.ok) {
        throw new Error("Failed to authenticate with backend");
      }

      const loginData = await loginResponse.json();
      const currentBackendToken = loginData.access_token;

      const acceptResponse = await fetch(
        `${Config.API_URL}/api/organization/accept-invitation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentBackendToken}`,
            "X-Firebase-Authorization": `Bearer ${firebaseToken}`,
          },
          body: JSON.stringify({ token }),
        },
      );

      if (!acceptResponse.ok) {
        const errorData = await acceptResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to accept invitation");
      }

      await refreshSubscriptionStatus();
      navigate("/home/dashboard", { replace: true });
    } catch (err: any) {
      console.error("Accept invitation error:", err);
      setApiError(
        err.message || "Failed to accept invitation. Please try again.",
      );
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError("");

    if (!name.trim()) {
      setApiError("Please enter your name");
      return;
    }

    if (password !== confirmPassword) {
      setApiError("Passwords do not match");
      return;
    }

    if (getPasswordScore(password) < 3) {
      setApiError("Please choose a stronger password");
      return;
    }

    if (!invitationData) {
      setApiError("Invalid invitation data");
      return;
    }

    setSubmitting(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        invitationData.email,
        password,
      );
      const user = userCredential.user;

      await setDoc(doc(db, "users", user.uid), {
        name: name.trim(),
        email: invitationData.email,
        createdAt: new Date().toISOString(),
      });

      const codeRes = await fetch(
        `${Config.API_URL}/api/auth/send-verification-code`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: invitationData.email,
            firebase_uid: user.uid,
          }),
        },
      );

      if (!codeRes.ok) {
        throw new Error("Failed to send verification code");
      }

      const codeData = await codeRes.json();
      await sendEmail({
        to: invitationData.email,
        template: verificationCodeEmail({ code: codeData.code }),
      });

      if (token) {
        sessionStorage.setItem("pendingOrgAcceptToken", token);
      }
      sessionStorage.setItem("pendingVerificationEmail", invitationData.email);
      sessionStorage.setItem("pendingVerificationName", name.trim());

      navigate("/verify-code", { replace: true });
    } catch (err: any) {
      console.error("Signup error:", err);

      if (err.code === "auth/email-already-in-use") {
        setApiError("This email is already registered. Please login instead.");
      } else if (err.code === "auth/weak-password") {
        setApiError("Password is too weak. Please use a stronger password.");
      } else if (err.code === "auth/invalid-email") {
        setApiError("Invalid email address.");
      } else {
        setApiError(
          err.message || "Failed to create account. Please try again.",
        );
      }

      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingPage variant="light" message="Loading invitation..." />;
  }

  if (error || !invitationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <TriangleAlert className="mx-auto h-12 w-12 text-red-500" />
            <h2 className="mt-4 text-2xl font-bold text-gray-900">
              Invalid Invitation
            </h2>
            <p className="mt-2 text-gray-600">{error}</p>
            <div className="mt-6">
              <SecondaryButton onClick={() => navigate("/login")}>
                Go to Login
              </SecondaryButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PublicNavbar />
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Join {invitationData.organization_name}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              You've been invited to join as a{" "}
              <span className="font-semibold">{invitationData.role}</span>
            </p>
          </div>

          <div className="bg-white shadow-lg rounded-lg p-8">
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0"></div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Email:</strong> {invitationData.email}
                    <br />
                    <strong>Role:</strong> {invitationData.role}
                  </p>
                </div>
              </div>
            </div>

            {invitationData.user_exists ? (
              invitationData.is_keycloak_user ? (
                // Keycloak existing user flow
                <div className="space-y-6">
                  {authUser?.email === invitationData.email && backendToken ? (
                    <form
                      onSubmit={handleExistingUserAccept}
                      className="space-y-6"
                    >
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-700">
                          You're logged in as <strong>{authUser.email}</strong>.
                          Click below to accept the invitation.
                        </p>
                      </div>
                      {apiError && (
                        <div className="rounded-md bg-red-50 p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <CircleX className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm text-red-800">{apiError}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      <PrimaryButton type="submit" disabled={submitting}>
                        {submitting
                          ? "Accepting Invitation..."
                          : "Accept Invitation"}
                      </PrimaryButton>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-700">
                          This invitation is for a PSDI account. Please log in
                          with PSDI to accept.
                        </p>
                      </div>
                      {apiError && (
                        <div className="rounded-md bg-red-50 p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <CircleX className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm text-red-800">{apiError}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      <PrimaryButton onClick={handleKeycloakLogin}>
                        Login with PSDI to Accept
                      </PrimaryButton>
                    </div>
                  )}
                </div>
              ) : (
                // flow for existing Firebase users
                <form onSubmit={handleExistingUserAccept} className="space-y-6">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-700">
                      You already have an account with us.{" "}
                      {currentUser
                        ? "Click the button below to accept the invitation."
                        : "Enter your password to accept the invitation."}
                    </p>
                  </div>

                  {!currentUser && (
                    <div>
                      <label
                        htmlFor="existingUserPassword"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Password
                      </label>
                      <div className="relative">
                        <input
                          id="existingUserPassword"
                          name="existingUserPassword"
                          type={showExistingUserPassword ? "text" : "password"}
                          required
                          value={existingUserPassword}
                          onChange={(e) =>
                            setExistingUserPassword(e.target.value)
                          }
                          className="mt-1 appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                          onClick={() =>
                            setShowExistingUserPassword(
                              !showExistingUserPassword,
                            )
                          }
                          tabIndex={-1}
                        >
                          {showExistingUserPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {apiError && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <CircleX className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-800">{apiError}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-4">
                    <PrimaryButton type="submit" disabled={submitting}>
                      {submitting
                        ? "Accepting Invitation..."
                        : "Accept Invitation"}
                    </PrimaryButton>
                  </div>
                </form>
              )
            ) : (
              // full signup form for new users
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Full Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1 appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                      placeholder="Create a password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <PasswordStrengthMeter password={password} minScore={3} />
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="Confirm your password"
                  />
                </div>

                {apiError && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <CircleX className="h-5 w-5 text-red-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{apiError}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex space-x-4">
                  <PrimaryButton type="submit" disabled={submitting}>
                    {submitting
                      ? "Creating Account..."
                      : "Create Account & Accept"}
                  </PrimaryButton>
                </div>
              </form>
            )}
          </div>

          <p className="text-center text-xs text-gray-500">
            By creating an account, you agree to our{" "}
            <a
              href="/legal/terms-of-service"
              className="text-blue-600 hover:text-blue-500"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="/legal/privacy-policy"
              className="text-blue-600 hover:text-blue-500"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OrganizationAccept;
